import { useState } from "react";
import PropTypes from "prop-types";
import { TbCode, TbPower } from "react-icons/tb";
import CustomInlineBanner from "../../../ui/CustomInlineBanner/CustomInlineBanner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../ui/CustomCard/CustomCard";
import CustomBadge from "../../../ui/CustomBadge/CustomBadge";
import CustomButton from "../../../ui/CustomButton/CustomButton";
import { useEnableApi } from "../../../../hooks/queries/useMerchantOpenApiQueries";
import { toastService } from "../../../../providers";
import { extractAxiosErrorMessage } from "../../../../services/utils/errorUtils";
import {
  notificationStyles,
  disableBannerStyle,
} from "../../styles/developerStyles";

export default function ApiAccessCard({ apiEnabled, disableApi, isDisabling }) {
  const { mutate: enableApi, isPending } = useEnableApi();
  const [showDisableConfirmation, setShowDisableConfirmation] = useState(false);

  const handleEnable = () => {
    enableApi(undefined, {
      onSuccess: () => {
        toastService.success("API access enabled successfully");
      },
      onError: (error) => {
        toastService.error(
          extractAxiosErrorMessage(error, "Failed to enable API access"),
        );
      },
    });
  };

  const handleDisableClick = () => setShowDisableConfirmation(true);
  const handleCancelDisable = () => setShowDisableConfirmation(false);

  const handleConfirmDisable = () => {
    disableApi(undefined, {
      onSuccess: () => {
        setShowDisableConfirmation(false);
        toastService.success("API access disabled successfully");
      },
      onError: (error) => {
        toastService.error(
          extractAxiosErrorMessage(error, "Failed to disable API access"),
        );
      },
    });
  };

  return (
    <Card className="bg-white border-[#e5e7eb] shadow-none">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold">API Access</CardTitle>
          <CustomBadge
            color={apiEnabled ? "green" : "gray"}
            variant="light"
            size="md"
            tt="none"
            leftSection={
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
            }
          >
            {apiEnabled ? "Enabled" : "Not Enabled"}
          </CustomBadge>
        </div>
        <CardDescription className="text-gray-500">
          Enable your account to start integrating PayKit&apos;s payment APIs
        </CardDescription>
      </CardHeader>

      <CardContent>
        {apiEnabled ? (
          <div className="flex flex-col gap-4">
            {!showDisableConfirmation && (
              <CustomInlineBanner styles={notificationStyles.info}>
                <p className="text-sm text-[var(--color-primary-600)]">
                  API access is active. Manage your credentials and callback URL
                  in the sections below.
                </p>
              </CustomInlineBanner>
            )}

            <div className={showDisableConfirmation ? "" : "border-t border-gray-100 pt-4"}>
              {showDisableConfirmation ? (
                <CustomInlineBanner styles={disableBannerStyle}>
                  <div className="flex flex-col gap-3">
                    <p className="text-sm text-red-800">
                      Disabling API access will revoke merchant-wide API access
                      immediately. All active integrations will stop working.
                      You can re-enable at any time.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <CustomButton
                        color="error"
                        size="sm"
                        radius="md"
                        loading={isDisabling}
                        onClick={handleConfirmDisable}
                      >
                        Yes, Disable
                      </CustomButton>
                      <CustomButton
                        variant="filled"
                        color="gray"
                        size="sm"
                        radius="md"
                        disabled={isDisabling}
                        onClick={handleCancelDisable}
                      >
                        Cancel
                      </CustomButton>
                    </div>
                  </div>
                </CustomInlineBanner>
              ) : (
                <CustomButton
                  variant="filled"
                  size="sm"
                  radius="md"
                  color="error"
                  leftSection={<TbPower size={15} />}
                  onClick={handleDisableClick}
                  className="self-start"
                >
                  Disable API Access
                </CustomButton>
              )}
            </div>
          </div>
        ) : (
          <CustomInlineBanner
            styles={notificationStyles.promo}
            icon={
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-cyan-500 to-blue-600">
                <TbCode size={22} color="white" />
              </div>
            }
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-800">
                  Activate API merchant access
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  Your account has been approved. Enable API access to generate
                  credentials and start integrating PayKit payment flows into
                  your platform.
                </p>
              </div>
              <CustomButton
                onClick={handleEnable}
                loading={isPending}
                color="dark"
                className="shrink-0 self-start sm:self-auto whitespace-nowrap"
              >
                Enable API Access
              </CustomButton>
            </div>
          </CustomInlineBanner>
        )}
      </CardContent>
    </Card>
  );
}

ApiAccessCard.propTypes = {
  apiEnabled: PropTypes.bool.isRequired,
  disableApi: PropTypes.func.isRequired,
  isDisabling: PropTypes.bool.isRequired,
};
